import React from "react";
import { Box, Typography, Paper, Divider, Chip, Stack } from "@mui/material";

import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import NumbersOutlinedIcon from "@mui/icons-material/NumbersOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";

function ComplaintReviewStep({
  complaintType,

  // =========================================================
  // ITEM
  // =========================================================
  selectedItem,
  selectedItemData,

  // =========================================================
  // DETAILS
  // =========================================================
  lotNumber,
  productionDate,
  expirationDate,
  quantity,
  quantityType,
  description,
  files,

  // =========================================================
  // CONTACT
  // =========================================================
  contactName,
  phoneNumber,

  userLang = "fr",
  t,
}) {
  // =========================================================
  // DEBUG
  // =========================================================

  console.log("selectedItem dans ComplaintReviewStep :", selectedItem);

  console.log("selectedItemData dans ComplaintReviewStep :", selectedItemData);

  // =========================================================
  // GENERAL
  // =========================================================

  const isProductComplaint = complaintType === "product";

  // =========================================================
  // ITEM NAME
  // =========================================================

  const getItemName = (item) => {
    if (!item) {
      return "";
    }

    const isFrench = userLang?.toLowerCase().startsWith("fr");

    if (isFrench) {
      return item.nomFr || item.nomEn || "";
    }

    return item.nomEn || item.nomFr || "";
  };

  // =========================================================
  // ITEM ID
  // =========================================================

  const getItemId = (item) => {
    if (!item) {
      return "";
    }

    return item.idItem ?? item.id ?? item.idItemAccPac ?? "";
  };

  // =========================================================
  // ORDER ID
  // =========================================================

  const getOrderId = (item) => {
    if (!item) {
      return "";
    }

    return item.idCommande ?? "";
  };

  // =========================================================
  // COMPLAINT TYPE
  // =========================================================

  const getComplaintTypeLabel = () => {
    if (complaintType === "product") {
      return t("Products");
    }

    if (complaintType === "transport") {
      return t("Transports");
    }

    return "";
  };

  // =========================================================
  // QUANTITY TYPE
  // =========================================================

  const getQuantityTypeLabel = () => {
    if (quantityType === "unit") {
      return t("Unit");
    }

    if (quantityType === "pack") {
      return t("Pack");
    }

    return quantityType;
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Box>
      {/* =====================================================
          HEADER
      ===================================================== */}

      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        {t("Review")}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t("ReviewComplaintBeforeSubmit")}
      </Typography>

      {/* =====================================================
          COMPLAINT TYPE
      ===================================================== */}

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 2,
        }}
      >
        <SectionHeader
          icon={<CheckCircleOutlineRoundedIcon />}
          title={t("ComplaintType")}
        />

        <Box sx={{ mt: 2 }}>
          <Chip
            label={getComplaintTypeLabel()}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* =====================================================
          ITEM
      ===================================================== */}

      {isProductComplaint && selectedItemData && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 2,
            borderRadius: 2,
          }}
        >
          <SectionHeader icon={<Inventory2OutlinedIcon />} title={t("Item")} />

          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: "rgba(25, 118, 210, 0.04)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
                gap: 2,
              }}
            >
              {/* =================================================
                  ITEM NAME
              ================================================= */}

              <ReviewField
                icon={<Inventory2OutlinedIcon />}
                label={t("Item")}
                value={getItemName(selectedItemData)}
              />

              {/* =================================================
                  ITEM ID
              ================================================= */}

              <ReviewField
                icon={<NumbersOutlinedIcon />}
                label="ID"
                value={getItemId(selectedItemData)}
              />
            </Box>
          </Box>

          {/* =====================================================
              ITEM DETAILS
          ===================================================== */}

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
              },
              gap: 2,
            }}
          >
            {/* =================================================
                LOT
            ================================================= */}

            {lotNumber && (
              <ReviewField
                icon={<NumbersOutlinedIcon />}
                label={t("LotNumber")}
                value={lotNumber}
              />
            )}

            {/* =================================================
                PRODUCTION DATE
            ================================================= */}

            {productionDate && (
              <ReviewField
                icon={<EventOutlinedIcon />}
                label={t("ProductionDate")}
                value={productionDate}
              />
            )}

            {/* =================================================
                EXPIRATION DATE
            ================================================= */}

            {expirationDate && (
              <ReviewField
                icon={<EventOutlinedIcon />}
                label={t("ExpirationDate")}
                value={expirationDate}
              />
            )}

            {/* =================================================
                QUANTITY
            ================================================= */}

            {quantity && (
              <ReviewField
                icon={<NumbersOutlinedIcon />}
                label={t("Quantity")}
                value={`${quantity} ${getQuantityTypeLabel()}`}
              />
            )}
          </Box>
        </Paper>
      )}

      {/* =====================================================
          DESCRIPTION
      ===================================================== */}

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 2,
        }}
      >
        <SectionHeader
          icon={<DescriptionOutlinedIcon />}
          title={t("ComplaintDescription")}
        />

        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            backgroundColor: "rgba(0, 0, 0, 0.025)",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {description || "—"}
          </Typography>
        </Box>
      </Paper>

      {/* =====================================================
          PICTURES
      ===================================================== */}

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 2,
        }}
      >
        <SectionHeader
          icon={<PhotoLibraryOutlinedIcon />}
          title={t("Pictures")}
        />

        {files?.length > 0 ? (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              mt: 2,
              flexWrap: "wrap",
              rowGap: 2,
            }}
          >
            {files.map((file, index) => {
              const fileURL = URL.createObjectURL(file);

              return (
                <Box
                  key={`${file.name}-${index}`}
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box
                    component="img"
                    src={fileURL}
                    alt={file.name}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t("NoPictures")}
          </Typography>
        )}
      </Paper>

      {/* =====================================================
          CONTACT
      ===================================================== */}

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 2,
        }}
      >
        <SectionHeader
          icon={<PersonOutlineOutlinedIcon />}
          title={t("ContactInformation")}
        />

        <Box
          sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
            },
            gap: 2,
          }}
        >
          <ReviewField
            icon={<PersonOutlineOutlinedIcon />}
            label={t("ContactName")}
            value={contactName}
          />

          <ReviewField
            icon={<PhoneOutlinedIcon />}
            label={t("PhoneNumber")}
            value={phoneNumber}
          />
        </Box>
      </Paper>

      {/* =====================================================
          FINAL MESSAGE
      ===================================================== */}

      <Paper
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: "rgba(46, 125, 50, 0.06)",
          border: "1px solid",
          borderColor: "rgba(46, 125, 50, 0.20)",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("ReviewConfirmationMessage")}
        </Typography>
      </Paper>
    </Box>
  );
}

/* =========================================================
   SECTION HEADER
========================================================= */

function SectionHeader({ icon, title }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          color: "primary.main",
        }}
      >
        {icon}
      </Box>

      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
    </Stack>
  );
}

/* =========================================================
   REVIEW FIELD
========================================================= */

function ReviewField({ icon, label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          color: "text.secondary",
          mt: 0.2,
        }}
      >
        {React.cloneElement(icon, {
          fontSize: "small",
        })}
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>

        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );
}

export default ComplaintReviewStep;
